import { MeasuresCalculator as MeasuresCalculatorBase, FormatUtils } from "@ortho-next/nextray-core";
import { NextRayMeasures } from "../Models/AppModel";
import { bindedModel } from "../Models/BindedModel";
import { EoCPlaneMechanical } from "../Tools/EoCPlane/EoCPlaneMechanical";
import { Main } from "./Main";

/**
 * Calculates measures.
 */
export class MeasuresCalculator extends MeasuresCalculatorBase implements NextRayMeasures {
	protected declare _main: Main;

	constructor(main: Main) {
		super(main);
	}

	public get eoc_ap_LPFA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.femur.LPFA.value!) : 0;
	}

	public get eoc_ap_mLDFA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.femur.mLDFA.value!) : 0;
	}

	public get eoc_ap_MPTA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.tibia.MPTA.value!) : 0;
	}

	public get eoc_ap_LDTA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.tibia.LDTA.value!) : 0;
	}

	public get eoc_ap_JLCA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.JLCA.value!) : 0;
	}

	public get eoc_ap_MAD(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.distance(this._main.toolsAP.clonedMechanicalAxis.MAD.value!) : 0;
	}

	public get eoc_ap_MNSA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.femur.MNSA.value!) : 0;
	}

	public get eoc_ap_MPFA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.femur.MPFA.value!) : 0;
	}

	public get eoc_ap_aLDFA(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.angleRadiants(this._main.toolsAP.clonedMechanicalAxis.femur.aLDFA.value!) : 0;
	}

	public get eoc_ap_kneeTranslation(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.distance(this._main.toolsAP.clonedMechanicalAxis.kneeTranslation.value!) : 0;
	}

	public get eoc_ap_fullLength(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.distance(this._main.toolsAP.clonedMechanicalAxis.length.value!) : 0;
	}

	public get eoc_ap_femurLength(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.distance(this._main.toolsAP.clonedMechanicalAxis.femur.length.value!) : 0;
	}

	public get eoc_ap_tibiaLength(): number {
		return this._main.toolsAP.clonedMechanicalAxis ? FormatUtils.distance(this._main.toolsAP.clonedMechanicalAxis.tibia.length.value!) : 0;
	}

	public get eoc_lt_PDFA(): number | undefined {
		const clonedMA = this._main.toolsLT.clonedMechanicalAxis;
		return clonedMA?.femur ? FormatUtils.angleRadiants(clonedMA.femur.PDFA.value!) : undefined;
	}

	public get eoc_lt_PPTA(): number | undefined {
		const clonedMA = this._main.toolsLT.clonedMechanicalAxis;
		return clonedMA?.tibia ? FormatUtils.angleRadiants(clonedMA.tibia.PPTA.value!) : undefined;
	}

	public get eoc_lt_ADTA(): number | undefined {
		const clonedMA = this._main.toolsLT.clonedMechanicalAxis;
		return clonedMA?.tibia ? FormatUtils.angleRadiants(clonedMA.tibia.ADTA.value!) : undefined;
	}

	public get eoc_lt_kneeTranslation(): number | undefined {
		const clonedMA = this._main.toolsLT.clonedMechanicalAxis;
		return clonedMA?.kneeTranslation?.value ? FormatUtils.distance(clonedMA.kneeTranslation.value) : undefined;
	}

	public get eoc_ap_axialLength(): number | undefined {
		if (!bindedModel.EOCCropVisible) return undefined;
		return this._main.toolsAP.EoCPlane ? FormatUtils.distance(this._main.toolsAP.EoCPlane.verticalTranslation!) : 0;
	}

	public get eoc_ap_cortexLength(): number | undefined {
		if (!bindedModel.EOCCropVisible) return undefined;
		return FormatUtils.distance((this._main.toolsAP.EoCPlane as EoCPlaneMechanical)?.cortexLength) ?? 0;
	}

	public get eoc_ap_angle(): number {
		return this._main.toolsAP.EoCPlane ? FormatUtils.angleRadiants(this._main.toolsAP.EoCPlane.angle) : 0;
	}

	public get eoc_ap_secondaryTransl(): number | undefined {
		if (!bindedModel.EOCCropVisible) return undefined;
		return this._main.toolsAP.EoCPlane ? FormatUtils.distance(this._main.toolsAP.EoCPlane.horizontalTranslation) : 0;
	}

	public get eoc_lt_axialLength(): number | undefined {
		if (!bindedModel.EOCCropVisible) return undefined;
		return this._main.toolsLT.EoCPlane ? FormatUtils.distance(this._main.toolsLT.EoCPlane.verticalTranslation!) : 0;
	}

	public get eoc_lt_cortexLength(): number | undefined {
		if (!bindedModel.EOCCropVisible) return undefined;
		return FormatUtils.distance((this._main.toolsLT.EoCPlane as EoCPlaneMechanical)?.cortexLength) ?? 0;
	}

	public get eoc_lt_angle(): number {
		return this._main.toolsLT.EoCPlane ? FormatUtils.angleRadiants(this._main.toolsLT.EoCPlane.angle) : 0
	}

	public get eoc_lt_secondaryTransl(): number | undefined {
		if (!bindedModel.EOCCropVisible) return undefined;
		return this._main.toolsLT.EoCPlane ? FormatUtils.distance(this._main.toolsLT.EoCPlane.horizontalTranslation) : 0;
	}
}
